import React, { useState, useEffect } from 'react';
import { analyticsAPI } from '../services/api';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const Costs = () => {
  const [costs, setCosts] = useState([]);
  const [period, setPeriod] = useState('daily');
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCosts();
  }, [period, days]);

  const loadCosts = async () => {
    setLoading(true);
    try {
      const response = await analyticsAPI.getCosts(period, days);
      setCosts(response.data.data.reverse());
    } catch (error) {
      console.error('Error loading costs:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 6
    }).format(value || 0);
  };

  const totalCost = costs.reduce((sum, item) => sum + parseFloat(item.total_cost || 0), 0);
  const totalSavings = costs.reduce((sum, item) => sum + parseFloat(item.total_savings || 0), 0);
  const totalCachedCost = costs.reduce((sum, item) => sum + parseFloat(item.cached_cost || 0), 0);
  const totalRegularCost = costs.reduce((sum, item) => sum + parseFloat(item.regular_input_cost || 0), 0);
  const totalOutputCost = costs.reduce((sum, item) => sum + parseFloat(item.output_cost || 0), 0);

  if (loading) {
    return <div className="loading">Caricamento...</div>;
  }

  return (
    <div>
      <div className="header">
        <h1>Analisi Costi</h1>
      </div>

      {/* Filtri */}
      <div className="chart-container" style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>Periodo</label>
            <select value={period} onChange={(e) => setPeriod(e.target.value)}>
              <option value="daily">Giornaliero</option>
              <option value="weekly">Settimanale</option>
              <option value="monthly">Mensile</option>
            </select>
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>Ultimi giorni</label>
            <select value={days} onChange={(e) => setDays(parseInt(e.target.value))}>
              <option value="7">7 giorni</option>
              <option value="30">30 giorni</option>
              <option value="60">60 giorni</option>
              <option value="90">90 giorni</option>
            </select>
          </div>
        </div>
      </div>

      {/* Statistiche totali */}
      <div className="stats-grid">
        <div className="stat-card">
          <h3>Costo Totale</h3>
          <div className="stat-value">{formatCurrency(totalCost)}</div>
        </div>
        <div className="stat-card">
          <h3>Risparmio Totale</h3>
          <div className="stat-value" style={{ color: '#28a745' }}>{formatCurrency(totalSavings)}</div>
          <div className="stat-change">
            {totalCost > 0 ? ((totalSavings / (totalCost + totalSavings)) * 100).toFixed(1) : 0}% risparmiato
          </div>
        </div>
        <div className="stat-card">
          <h3>Costo Input (Cache)</h3>
          <div className="stat-value">{formatCurrency(totalCachedCost)}</div>
        </div>
        <div className="stat-card">
          <h3>Costo Input (Regular)</h3>
          <div className="stat-value">{formatCurrency(totalRegularCost)}</div>
        </div>
        <div className="stat-card">
          <h3>Costo Output</h3>
          <div className="stat-value">{formatCurrency(totalOutputCost)}</div>
        </div>
        <div className="stat-card">
          <h3>Costo Senza Cache</h3>
          <div className="stat-value">{formatCurrency(totalCost + totalSavings)}</div>
          <div className="stat-change negative">
            Evitato grazie alla cache
          </div>
        </div>
      </div>

      {/* Grafico andamento costi */}
      <div className="chart-container">
        <h2>Andamento Costi nel Tempo</h2>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={costs}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="period" />
            <YAxis />
            <Tooltip formatter={(value) => formatCurrency(value)} />
            <Legend />
            <Line type="monotone" dataKey="total_cost" stroke="#667eea" name="Costo Totale" strokeWidth={2} />
            <Line type="monotone" dataKey="total_savings" stroke="#28a745" name="Risparmio" strokeWidth={2} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Grafico breakdown costi */}
      <div className="chart-container">
        <h2>Breakdown Costi per Periodo</h2>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={costs}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="period" />
            <YAxis />
            <Tooltip formatter={(value) => formatCurrency(value)} />
            <Legend />
            <Bar dataKey="cached_cost" stackId="a" fill="#4facfe" name="Input (Cache)" />
            <Bar dataKey="regular_input_cost" stackId="a" fill="#667eea" name="Input (Regular)" />
            <Bar dataKey="output_cost" stackId="a" fill="#764ba2" name="Output" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Tabella dettagliata */}
      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              <th>Periodo</th>
              <th>Chat</th>
              <th>Costo Cache</th>
              <th>Costo Input</th>
              <th>Costo Output</th>
              <th>Costo Totale</th>
              <th>Risparmio</th>
              <th>% Risparmio</th>
            </tr>
          </thead>
          <tbody>
            {costs.map((item, index) => {
              const savingsPercentage = item.total_cost > 0 
                ? ((item.total_savings / (parseFloat(item.total_cost) + parseFloat(item.total_savings))) * 100).toFixed(1)
                : 0;
              
              return (
                <tr key={index}>
                  <td><strong>{item.period}</strong></td>
                  <td>{item.chat_count}</td>
                  <td>{formatCurrency(item.cached_cost)}</td>
                  <td>{formatCurrency(item.regular_input_cost)}</td>
                  <td>{formatCurrency(item.output_cost)}</td>
                  <td><strong>{formatCurrency(item.total_cost)}</strong></td>
                  <td style={{ color: '#28a745' }}>{formatCurrency(item.total_savings)}</td>
                  <td style={{ color: '#28a745' }}><strong>{savingsPercentage}%</strong></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Costs;

